/**
 * The browser's view of the owner's workspace on the relay path: found, started, paused and reset through
 * `/api/workspace`, and the ops client every chat read and write goes through. One store per page; the lifecycle
 * controls and the chat hooks subscribe to the same workspace id.
 */
'use client'
import { useSyncExternalStore } from 'react'
import { z } from 'zod'
import { WorkspaceClient, type WorkspaceClientOptions } from '@softmax/observatory-client/workspaces'
import { workspaceState, type WorkspaceState } from './contract'
import { forgetWorkspace, OpsClient, RelayError } from './ops'

type Workspace =
  | { status: 'unknown' }
  | { status: 'known'; workspace: WorkspaceState | null }
  /** The platform answered 410 for the id this tab holds: the tab is stale until it reloads. */
  | { status: 'retired'; workspaceId: string }
let state: Workspace = { status: 'unknown' }
let reading: Promise<WorkspaceState | null> | undefined
/** Workspace ids whose saved sends this page has already offered again; a re-read never resumes twice. */
const resumed = new Set<string>()
const listeners = new Set<() => void>()

function set(next: Workspace) {
  state = next
  for (const listener of listeners) listener()
}

let platform = new WorkspaceClient({ baseUrl: '/api/observatory' })

/** Where this page reaches the platform. An embedding site passes its own same-origin proxy before any chat opens. */
export function configureRelay(options: WorkspaceClientOptions) {
  platform = new WorkspaceClient(options)
}

export const ops = new OpsClient({
  operation: (...args) => platform.operation(...args),
  storage: typeof localStorage === 'undefined' ? (new Map() as unknown as Storage) : localStorage,
  onRetired: (workspaceId) => {
    reading = Promise.reject(retiredError())
    reading.catch(() => {})
    set({ status: 'retired', workspaceId })
  },
})

const retiredError = () =>
  new RelayError('workspace_retired', 'This workspace was reset. Reload to open your current workspace.', 410)

async function workspaceRequest(init: RequestInit = {}, eager = false) {
  let workspace = await platform.current(init.signal ?? undefined)
  // The wake endpoint rechecks current owner intent, including a Pause in another tab.
  if (eager && workspace?.backend === 'kubernetes' && workspace.status === 'suspended' && !workspace.hold) {
    await platform.wake(workspace, init.signal ?? undefined)
    workspace = await platform.current(init.signal ?? undefined)
  }
  if (init.method === 'POST') {
    const { desired_state } = z
      .object({ desired_state: z.enum(['running', 'stopped']).optional() })
      .parse(JSON.parse(String(init.body)))
    if (desired_state === 'stopped') {
      if (!workspace) throw new RelayError('workspace_missing', 'You have no workspace to pause.', 404)
      workspace = await platform.setLifecycle(workspace, 'stopped')
    } else {
      workspace = await platform.ensureRunning(init.signal ?? undefined)
    }
  }
  return (
    workspace &&
    workspaceState.parse({
      workspace_id: workspace.provisioning_id,
      backend: workspace.backend,
      status: workspace.status,
      connected: workspace.connected,
      can_stop: workspace.can_stop,
      can_reset: workspace.can_reset,
      detail: workspace.detail,
    })
  )
}

async function apply(request: Promise<WorkspaceState | null>) {
  const workspace = await request
  set({ status: 'known', workspace })
  return workspace
}

/** Read once per page and wake retained idle compute. New accounts and explicit pauses wait for user action. */
export function readWorkspace(): Promise<WorkspaceState | null> {
  reading ??= apply(workspaceRequest({}, true)).then(
    (workspace) => {
      // Sends saved before a reload are delivered again with their original ids, once per page.
      if (workspace && !resumed.has(workspace.workspace_id)) {
        resumed.add(workspace.workspace_id)
        void ops.resume(workspace.workspace_id)
      }
      return workspace
    },
    (error: unknown) => {
      reading = undefined
      throw error
    }
  )
  return reading
}

export function refreshWorkspace() {
  reading = apply(workspaceRequest()).catch((error: unknown) => {
    reading = undefined
    throw error
  })
  return reading
}

/** Provision when absent, resume when stopped. */
export const startWorkspace = (signal?: AbortSignal) =>
  (reading = apply(
    workspaceRequest({
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
      signal: signal ?? null,
    })
  ))

export const pauseWorkspace = () =>
  (reading = apply(
    workspaceRequest({
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ desired_state: 'stopped' }),
    })
  ))

/** Delete the exact workspace; the platform forgets its id and so does this browser. */
export async function resetWorkspace(workspaceId: string) {
  const workspace = await platform.current()
  if (!workspace || workspace.provisioning_id !== workspaceId)
    throw new RelayError('unknown_workspace', 'That workspace is not yours.', 404)
  const result = await platform.reset(workspace)
  if (result.status === 'deleted') {
    forgetWorkspace(ops.options.storage, workspaceId)
    reading = undefined
    set({ status: 'unknown' })
    window.dispatchEvent(new Event('workspace-reset'))
  }
  return result
}

/** The relay id every operation is addressed to; a typed failure when the owner has no workspace yet. */
export async function workspaceId() {
  const workspace = await readWorkspace()
  if (!workspace)
    throw new RelayError('workspace_missing', 'Your workspace has not started. Start it from the header.', 409)
  if (workspace.backend !== 'kubernetes')
    throw new RelayError(
      'legacy_backend',
      'This workspace is on the previous system. Reset it to create a new workspace.',
      409
    )
  return workspace.workspace_id
}

export function useWorkspace(): Workspace {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
    () => state,
    () => state
  )
}
